import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import { AvatarComponent } from '../../../../ui-components/lib/avatar/avatar.component';
import { StatComponent } from '../../../../ui-components/lib/stat/stat.component';
import { SkeletonDirective } from '../../../../ui-components/directives/skeleton/skeleton.directive';
import { filterQueryParamName } from '../../../../ui-components/lib/table/table.model';
import { EMPLOYEE_ROUTES } from '../../../common/constants/employee.routing.constants';
import { FEATURE_FLAGS } from '../../../common/constants/feature-flags.constants';
import { RequestTypeEnum } from '../../../common/enums/request-type';
import { Insight } from '../../../common/models/insight.model';
import { StepTask } from '../../../common/models/step-task.model';
import { MyInboxTabsInsight } from '../../../common/models/myinbox-insight.model';
import { AuthService } from '../../../common/services/auth/auth.service';
import { FeatureFlagsService } from '../../../common/services/feature-flags/feature-flags.service';
import { MyInboxService } from '../../../common/services/my-inbox/my-inbox.service';
import {
  ApprovalRow,
  displayCount,
  greetingKey,
  HomeTile,
  insightValue,
  Loadable,
  relativeAge,
  SHOWN,
  TABS,
} from './home-constant';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../common/helpers/system-tabs-layout/system-tabs-layout';

@Component({
  selector: 'lib-employee-home',
  imports: [
    RouterLink,
    TranslocoPipe,
    StatComponent,
    AvatarComponent,
    SkeletonDirective,
  ],
  templateUrl: './employee-home.component.html',
  providers: [provideSystemTabsLayout()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmployeeHomeComponent implements OnInit {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  private readonly inbox = inject(MyInboxService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlags = inject(FeatureFlagsService);

  readonly approvalSkeletonRows = [1, 2, 3];
  readonly inboxLink = `/${EMPLOYEE_ROUTES.MY_INBOX.LIST}`;
  readonly newRequestLink = `/${EMPLOYEE_ROUTES.REQUESTS.LIST}`;
  readonly assignedTab = { tab: TABS.assignedToMe };
  readonly firstName =
    inject(AuthService).centralUser()?.first_name?.trim() ?? '';
  readonly greeting = greetingKey(new Date().getHours());
  readonly dateLine = new Intl.DateTimeFormat(this.transloco.getActiveLang(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date());

  readonly approvals = signal<Loadable<StepTask[]>>(undefined);
  private readonly ownAssigned = signal<Loadable<number>>(undefined);
  private readonly delegated = signal<Loadable<number>>(0);
  private readonly overdue = signal<Loadable<number>>(undefined);
  private readonly open = signal<Loadable<number>>(undefined);
  private readonly participated = signal<Loadable<number>>(undefined);

  // Same figure as My Inbox's "Assigned to me" badge. Delegated tasks are included only while they share that tab.
  private readonly awaiting = computed<Loadable<number>>(() => {
    const own = this.ownAssigned();
    const delegated = this.delegated();
    if (own === null || delegated === null) return null;
    if (own === undefined || delegated === undefined) return undefined;
    return own + delegated;
  });
  readonly awaitingCount = this.awaiting;
  readonly showViewAll = computed(() => (this.awaiting() ?? 0) > SHOWN);
  readonly tiles = computed<HomeTile[]>(() => {
    const tile = (item: Omit<HomeTile, 'display'>): HomeTile => ({
      ...item,
      display: displayCount(item.value),
    });
    return [
      tile({
        key: 'awaiting',
        label: 'employee_home_tile_awaiting',
        icon: 'inbox',
        value: this.awaiting(),
        queryParams: { tab: TABS.assignedToMe },
      }),
      tile({
        key: 'overdue',
        label: 'employee_home_tile_overdue',
        icon: 'clock',
        value: this.overdue(),
        queryParams: {
          tab: TABS.assignedToMe,
          [filterQueryParamName]: 'sla_exceeded',
        },
      }),
      tile({
        key: 'open',
        label: 'employee_home_tile_open',
        icon: 'in_progress',
        value: this.open(),
        queryParams: {
          tab: TABS.createdByMe,
          [filterQueryParamName]: 'in_progress',
        },
      }),
      tile({
        key: 'participated',
        label: 'employee_home_tile_participated',
        icon: 'multi_users',
        value: this.participated(),
        queryParams: { tab: TABS.allParticipated },
      }),
    ];
  });

  readonly approvalRows = computed<ApprovalRow[]>(() => {
    const steps = this.approvals();
    if (!steps) return [];
    const lang = this.transloco.getActiveLang();
    return steps.map(step => ({
      id: step.id,
      creatorName: step.flow.creator?.name ?? '',
      creatorImage: step.flow.creator?.image?.url ?? '',
      subject: step.flow.request_subject || step.flow.request_id,
      flowName: step.flow.name,
      stepName: step.name,
      overdue:
        !!step.due_date && new Date(step.due_date).getTime() < Date.now(),
      age: relativeAge(step.created_at, lang),
      link: `${this.inboxLink}/${step.id}/${RequestTypeEnum.ACTION}/${step.flow.id}`,
    }));
  });

  ngOnInit(): void {
    this.loadAssignedSteps();
    this.loadDelegatedTasks();
    this.loadOverdueTasks();
    this.loadOpenRequests();
    this.loadParticipatedTasks();
  }

  private loadAssignedSteps(): void {
    this.load(
      this.inbox.getStepsAssignToMe(
        1,
        SHOWN,
        '',
        { key: 'created_at][type', value: 'asc' },
        ''
      ),
      res => {
        this.approvals.set(res.data);
        this.ownAssigned.set(res.meta.total);
      },
      () => {
        this.approvals.set(null);
        this.ownAssigned.set(null);
      }
    );
  }

  private loadDelegatedTasks(): void {
    if (
      this.featureFlags.getFeatureFlagValue(
        FEATURE_FLAGS.INBOX.delegated_to_me_tab
      )
    ) {
      return;
    }
    this.delegated.set(undefined);
    this.load(
      this.inbox.getTabsInsight() as Observable<{ data: MyInboxTabsInsight }>,
      res => this.delegated.set(res.data.delegated_tasks_count ?? 0),
      () => this.delegated.set(null)
    );
  }

  private loadOverdueTasks(): void {
    this.load(
      this.inbox.getAssignedToMeInsights(),
      res =>
        this.overdue.set(insightValue(res.data, 'total_current_overdue_tasks')),
      () => this.overdue.set(null)
    );
  }

  private loadOpenRequests(): void {
    this.load(
      this.inbox.getFlowsCreatedByMeInsights(),
      res => this.open.set(insightValue(res.data, 'in_progress')),
      () => this.open.set(null)
    );
  }

  private loadParticipatedTasks(): void {
    this.load(
      this.inbox.getAllParticipatedInsight() as Observable<{ data: Insight[] }>,
      res =>
        this.participated.set(
          res.data.reduce((sum, i) => sum + Number(i.value), 0)
        ),
      () => this.participated.set(null)
    );
  }

  private load<T>(
    source: Observable<T>,
    next: (res: T) => void,
    error: () => void
  ): void {
    source.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next, error });
  }
}
